import React from "react";
import { AlertCircle, Info } from "lucide-react";
import { emitLifecycle } from "@/lib/lifecycle";

/**
 * AnalyzeErrorBoundary — catches React hydration crashes inside the
 * Analyze page result block. Shipped after the "MU silent crash"
 * incident (Phase 6.AG) where the backend returned a valid HTTP 200
 * payload but a child component threw on a null KPI / null
 * institutional sub-field, causing the entire analyze view to white-
 * screen with no useful signal for the operator.
 *
 * Behavior:
 *   * Renders the children normally on the happy path
 *   * On any thrown error during render: displays a clean "Insufficient
 *     data" UI to regular users (no scary stack traces) AND a full
 *     error+stack panel to admins so the exact crashing component can
 *     be identified instantly
 *   * Provides a "Retry" CTA that re-mounts the subtree (e.g. after the
 *     user clears cache + re-analyzes)
 *   * NEVER blocks the rest of the page (header, footer, navigation
 *     remain interactive)
 */
export default class AnalyzeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, auditId: null, reported: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  async componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[AnalyzeErrorBoundary] caught render crash", error, info);
    this.setState({ info });
    // Phase 6.AL — Also emit a `render_failed` lifecycle event so the
    // Owner Console "Ticker provenance trace" panel can show the
    // render-failed step alongside the response_received step,
    // explaining WHY a 200 OK response ended up showing "Something
    // went wrong" UI.
    try {
      const lastReqId = (
        (typeof window !== "undefined" && window.__STOCKAILY_LAST_REQUEST_IDS__?.[0]?.request_id) ||
        this.props.requestId || null
      );
      const failingComponent = (info?.componentStack || "")
        .split("\n").map((s) => s.trim()).filter(Boolean)[0] || null;
      emitLifecycle("render_failed", {
        ticker: this.props.ticker || null,
        request_id: lastReqId,
        extra: {
          boundary: "analyze",
          component: failingComponent,
          error_name: error?.name || "Error",
          error_message: String(error?.message || error || "").slice(0, 500),
          first_stack_frame: String(error?.stack || "").split("\n")[1]?.trim() || null,
          schema_valid: this.props.schemaValid ?? null,
          data_source_quality: this.props.dataSourceQuality || null,
          ai_status: this.props.aiStatus || null,
        },
      });
    } catch { /* swallow */ }
    // Phase 6.AH — POST to backend so admins see this crash on the Owner
    // Console even without browser devtools access. Includes the
    // backend-issued request_id so they can pull the original analyze
    // response from db.http_audit_log / db.analysis_cache.
    try {
      const backend = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
      const url = `${backend}/api/_health/frontend_error`;
      const lastReqId = (
        (typeof window !== "undefined" && window.__STOCKAILY_LAST_REQUEST_IDS__?.[0]?.request_id) ||
        this.props.requestId || null
      );
      const body = {
        ticker: this.props.ticker || null,
        boundary: "analyze",
        component: (info?.componentStack || "").split("\n").map((s) => s.trim()).filter(Boolean)[0] || null,
        error_name: error?.name || "Error",
        error_message: String(error?.message || error || "").slice(0, 500),
        error_stack: String(error?.stack || "").slice(0, 4000),
        component_stack: String(info?.componentStack || "").slice(0, 4000),
        user_agent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
        url: (typeof window !== "undefined") ? window.location.href : null,
        request_id: lastReqId,
        schema_valid: this.props.schemaValid ?? null,
        data_source_quality: this.props.dataSourceQuality || null,
        ai_status: this.props.aiStatus || null,
      };
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      this.setState({ auditId: json?.audit_id || null, reported: true });
    } catch (postErr) {
      // eslint-disable-next-line no-console
      console.error("[AnalyzeErrorBoundary] failed to report", postErr);
    }
  }

  reset = () => {
    this.setState({ error: null, info: null, auditId: null, reported: false });
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    const { error, info, auditId, reported } = this.state;
    const isAdmin = !!this.props.isAdmin;
    const ticker = this.props.ticker || "this stock";
    return (
      <div
        className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/5 p-5"
        data-testid="analyze-render-error-boundary"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <div
              className="text-base font-semibold text-amber-700 mb-1"
              data-testid="analyze-render-error-title"
            >
              Limited fundamental data for {ticker}
            </div>
            <div className="text-sm text-muted-foreground leading-relaxed">
              We received the analysis from our backend, but the response was
              missing some key fields needed to render the full breakdown.
              This usually happens when an upstream financial data endpoint
              is plan-restricted or temporarily unavailable. Try again in a
              few minutes, or check the Owner Console for endpoint health.
            </div>
            <button
              type="button"
              onClick={this.reset}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors"
              data-testid="analyze-render-error-retry"
            >
              Retry render
            </button>
            {auditId ? (
              <div className="mt-2 text-[11px] text-muted-foreground" data-testid="analyze-render-error-audit-id">
                Trace id: <code className="font-mono">{auditId}</code> · search Owner Console &raquo; Recent frontend errors
              </div>
            ) : reported === false && (this.state.error) ? (
              <div className="mt-2 text-[11px] text-muted-foreground">
                (Could not POST this crash to the backend — check your network tab)
              </div>
            ) : null}
          </div>
        </div>
        {isAdmin ? (
          <div
            className="mt-4 pt-4 border-t border-amber-500/30 text-xs"
            data-testid="analyze-render-error-admin-detail"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-rose-600" />
              <span className="font-semibold text-rose-700">
                [ADMIN] React render crash
              </span>
            </div>
            <div className="mb-1">
              <span className="text-muted-foreground">Error:</span>{" "}
              <code className="text-rose-700 text-[11px]">
                {error?.name || "Error"}: {error?.message || String(error)}
              </code>
            </div>
            {error?.stack ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground text-[11px]">
                  JS stack trace
                </summary>
                <pre
                  className="mt-1 whitespace-pre-wrap break-all bg-black/60 text-rose-200 p-2 rounded text-[10px] max-h-72 overflow-auto"
                  data-testid="analyze-render-error-stack"
                >
                  {String(error.stack)}
                </pre>
              </details>
            ) : null}
            {info?.componentStack ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground text-[11px]">
                  React component stack
                </summary>
                <pre
                  className="mt-1 whitespace-pre-wrap break-all bg-black/40 text-amber-200 p-2 rounded text-[10px] max-h-72 overflow-auto"
                  data-testid="analyze-render-error-component-stack"
                >
                  {String(info.componentStack)}
                </pre>
              </details>
            ) : null}
            <div className="mt-2 text-[10px] text-muted-foreground">
              The exact failing component is in the "React component stack"
              dropdown. Add a null-guard there + redeploy.
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
